import React, { useState } from 'react';
import {
  SpaceBetween,
  Spinner,
  Modal,
  Box,
  Button,
  StatusIndicator,
} from '@cloudscape-design/components';
import './ImageGallery.css';
import { set } from 'lodash';

// Image item structure for gallery display
export interface ImageGalleryItem {
  id: string;
  fileName?: string;
  imageUrl?: string;
}

interface ImageGalleryProps {
  items: ImageGalleryItem[];
  onDelete?: (mediaId: string) => void;
  readOnly?: boolean;
  loading?: boolean;
}

// Gallery component with thumbnail grid, modal preview, and download functionality
const ImageGallery: React.FC<ImageGalleryProps> = ({
  items,
  onDelete,
  readOnly = false,
  loading = false,
}) => {
  const [lastClicked, setLastClicked] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<ImageGalleryItem | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<
    'loading' | 'error' | 'success' | null
  >(null);

  // Open modal with full-size image preview
  const handleImageClick = (item: ImageGalleryItem) => {
    if (!loading) {
      setSelectedImage(item);
      setIsModalVisible(true);
    }
  };

  // Download image using programmatic anchor click
  const handleDownload = async () => {
    if (selectedImage?.imageUrl) {
      setDownloadStatus('loading');
      try {
        const link = document.createElement('a');
        link.href = selectedImage.imageUrl;
        link.download = selectedImage.fileName || 'image.jpg';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloadStatus('success');
      } catch (error) {
        console.error('Download failed:', error);
        setDownloadStatus('error');
      }
    }
  };

  return (
    <>
      {/* Thumbnail grid */}
      <SpaceBetween data-testid="image-gallery-container" size="s" direction="horizontal">
        {items.map((item, index) => (
          <div
            key={item.id}
            data-testid={`image-gallery-item-${item.id}`}
            className={`image-gallery-item ${loading && lastClicked === item.id ? 'disabled' : ''}`}
          >
            <div
              data-testid={`image-container-${item.id}`}
              className="image-container"
              onClick={() => handleImageClick(item)}
              role="button"
              tabIndex={0}
            >
              <img
                data-testid={`image-thumbnail-${item.id}`}
                src={item.imageUrl}
                className="image-gallery-thumbnail"
                style={{ opacity: loading ? 0.5 : 1 }}
                alt={item.fileName || 'Image preview'}
              />
              {loading && lastClicked === item.id && (
                <div
                  data-testid={`spinner-overlay-${item.id}`}
                  className="spinner-overlay"
                >
                  <Spinner data-testid={`loading-spinner-${item.id}`} size="large" />
                </div>
              )}
            </div>
            {/* Delete button (only in edit mode) */}
            {!readOnly && onDelete && (
              <button
                data-testid={`delete-button-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setLastClicked(item.id);
                  onDelete(item.id);
                }}
                className="image-gallery-delete-btn"
                disabled={loading}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </SpaceBetween>

      {/* Full-size image modal with download */}
      <Modal
        data-testid="image-preview-modal"
        visible={isModalVisible}
        onDismiss={() => {
          setIsModalVisible(false);
          setDownloadStatus(null);
        }}
        size="large"
        header={'Image Preview'}
        footer={
          <Box data-testid="modal-footer" float="right">
            <SpaceBetween data-testid="modal-actions" direction="horizontal" size="xs">
              {downloadStatus === 'error' && (
                <StatusIndicator data-testid="download-error-status" type="error">
                  Download failed
                </StatusIndicator>
              )}
              <Button
                data-testid="cancel-button"
                onClick={() => {
                  setIsModalVisible(false);
                  setDownloadStatus(null);
                }}
                variant="link"
              >
                Cancel
              </Button>
              <Button
                data-testid="download-button"
                onClick={handleDownload}
                variant="primary"
                iconName="download"
                loading={downloadStatus === 'loading'}
                disabled={downloadStatus === 'loading'}
              >
                Download
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box data-testid="modal-content" padding="l">
          {selectedImage && (
            <div data-testid="modal-image-container" className="modal-image-container">
              <img
                data-testid="modal-image"
                src={selectedImage.imageUrl}
                alt={selectedImage.fileName || 'Image preview'}
                className="modal-image"
              />
            </div>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ImageGallery;
